/**
 * <ry-color-input>
 *
 * Input-style color picker with swatch preview and editable hex value.
 * Composes ry-color-picker internally for the picker panel.
 *
 * Usage:
 * <ry-color-input value="#ff0000"></ry-color-input>
 * <ry-color-input value="#ff0000" opacity></ry-color-input>
 */

import { RyElement } from '../core/ry-element.js';
import type { RyColorPicker, RGB, HSL, HSV } from './ry-color-picker.js';

export class RyColorInput extends RyElement {
  #swatch: HTMLElement | null = null;
  #input: HTMLInputElement | null = null;
  #panel: HTMLElement | null = null;
  #picker: RyColorPicker | null = null;

  static observedAttributes = ['value', 'format', 'opacity', 'disabled', 'placeholder'] as const;

  setup(): void {
    this.#buildMarkup();
    this.#bindEvents();
    this.#syncFromValue();
    // Start closed
    this.state = 'closed';
  }

  #buildMarkup(): void {
    const showAlpha = this.hasAttribute('opacity');
    const format = this.getAttribute('format') || 'hex';
    const value = this.getAttribute('value') || '#000000';
    const placeholder = this.getAttribute('placeholder') || '#000000';

    this.innerHTML = `
      <div data-ry-target="trigger" class="ry-color-input__trigger">
        <button data-ry-target="swatch" type="button" class="ry-color-input__swatch">
          <span data-ry-target="swatch-color" class="ry-color-input__swatch-color"></span>
        </button>
        <input
          data-ry-target="input"
          type="text"
          class="ry-color-input__input"
          value="${value.startsWith('#') ? value : '#' + value}"
          placeholder="${placeholder.startsWith('#') ? placeholder : '#' + placeholder}"
          spellcheck="false"
          autocomplete="off"
          maxlength="7"
        />
      </div>
      <div data-ry-target="panel" class="ry-color-input__panel">
        <ry-color-picker
          data-ry-target="picker"
          inline
          value="${value}"
          format="${format}"
          ${showAlpha ? 'opacity' : ''}
        ></ry-color-picker>
      </div>
    `;

    this.#swatch = this.$('[data-ry-target="swatch"]');
    this.#input = this.$('[data-ry-target="input"]');
    this.#panel = this.$('[data-ry-target="panel"]');
    this.#picker = this.$('[data-ry-target="picker"]') as RyColorPicker;
  }

  #bindEvents(): void {
    // Swatch click opens/closes panel
    if (this.#swatch) {
      this.on(this.#swatch, 'click', this.#handleSwatchClick);
    }

    // Input changes update the picker
    if (this.#input) {
      this.on(this.#input, 'input', this.#handleInputChange);
      this.on(this.#input, 'keydown', this.#handleInputKeydown);
      this.on(this.#input, 'focus', this.#handleInputFocus);
    }

    // Picker changes update the input
    if (this.#picker) {
      this.on(this.#picker, 'ry:input', this.#handlePickerInput);
      this.on(this.#picker, 'ry:change', this.#handlePickerChange);
    }

    // Click outside to close
    this.on(document, 'click', this.#handleDocumentClick);

    // Escape to close
    this.on(document, 'keydown', this.#handleDocumentKeydown);
  }

  #handleSwatchClick = (): void => {
    if (this.disabled) return;
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  };

  #handleInputFocus = (): void => {
    if (this.disabled) return;
    this.open();
  };

  #handleInputChange = (): void => {
    if (!this.#input || !this.#picker) return;

    let value = this.#input.value;
    const cursorPos = this.#input.selectionStart ?? value.length;

    // Filter to only # and hex characters
    const filtered = value.replace(/[^#a-fA-F0-9]/g, '');

    // Normalize: ensure single # at start
    const hexPart = filtered.replace(/#/g, '').slice(0, 6);
    const normalized = '#' + hexPart;

    // Only update input if we actually changed something
    if (value !== normalized) {
      this.#input.value = normalized;
      // Keep cursor in reasonable position
      const newPos = Math.min(cursorPos, normalized.length);
      this.#input.setSelectionRange(newPos, newPos);
    }

    // Update picker with expanded color
    const expanded = this.#expandPartialHex(normalized);
    if (expanded) {
      this.#picker.setColor(expanded);
      this.#updateSwatch();
      if (this.#isCompleteColor(normalized)) {
        this.#syncValueAttribute();
      }
      this.emit('input', { value: this.value });
    }
  };

  #expandPartialHex(value: string): string | null {
    let color = value;
    if (!color.startsWith('#')) color = '#' + color;

    const hex = color.slice(1);
    if (!/^[a-fA-F0-9]+$/.test(hex) || hex.length === 0) {
      return null;
    }

    // 6 chars - use as-is
    if (hex.length === 6) {
      return color;
    }

    // 1-3 chars - pad to 3, then CSS shorthand expand
    // #f → #fff → #ffffff
    // #fa → #faa → #ffaaaa
    // #f00 → #ff0000
    if (hex.length <= 3) {
      const padded = hex.padEnd(3, hex[hex.length - 1]!);
      const [r, g, b] = padded.split('');
      return `#${r}${r}${g}${g}${b}${b}`;
    }

    // 4-5 chars - pad with zeros
    // #f00a → #f00a00
    // #f00ab → #f00ab0
    return '#' + hex.padEnd(6, '0');
  }

  #isCompleteColor(value: string): boolean {
    // Only 3 or 6 char hex are "complete"
    if (/^#[a-fA-F0-9]{6}$/.test(value)) return true;
    if (/^#[a-fA-F0-9]{3}$/.test(value)) return true;
    return false;
  }

  #handleInputKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.#handleInputChange();
      this.emit('change', { value: this.value });
    }
  };

  #handlePickerInput = (): void => {
    if (!this.#picker || !this.#input) return;

    // Don't overwrite input if user is typing in it
    if (document.activeElement !== this.#input) {
      this.#input.value = this.#picker.value;
    }

    this.#updateSwatch();
    this.#syncValueAttribute();
    this.emit('input', { value: this.value });
  };

  #handlePickerChange = (): void => {
    this.emit('change', { value: this.value });
  };

  #handleDocumentClick = (e: Event): void => {
    if (this.state !== 'open') return;
    if (!this.contains(e.target as Node)) {
      this.close();
    }
  };

  #handleDocumentKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.state === 'open') {
      this.close();
      this.#swatch?.focus();
    }
  };

  open(): void {
    this.#positionPanel();
    this.state = 'open';
  }

  close(): void {
    this.state = 'closed';
    this.removeAttribute('data-ry-position');
  }

  #positionPanel(): void {
    if (!this.#panel) return;

    const rect = this.getBoundingClientRect();
    const panelHeight = 280;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < panelHeight && spaceAbove > spaceBelow) {
      this.setAttribute('data-ry-position', 'top');
    } else {
      this.setAttribute('data-ry-position', 'bottom');
    }
  }

  #updateSwatch(): void {
    const swatchColor = this.$('[data-ry-target="swatch-color"]');
    if (swatchColor && this.#picker) {
      const rgb = this.#picker.rgb;
      const alpha = this.hasAttribute('opacity') ? this.#picker.alpha / 100 : 1;
      (swatchColor as HTMLElement).style.backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    }
  }

  #syncFromValue(): void {
    const value = this.getAttribute('value') || '#000000';
    if (this.#picker) {
      this.#picker.setColor(value);
      if (this.#input) {
        // Always show as hex with #
        const hex = this.#picker.value;
        this.#input.value = hex.startsWith('#') ? hex : '#' + hex;
      }
      this.#updateSwatch();
    }
  }

  #syncValueAttribute(): void {
    if (this.#picker) {
      const formatted = this.#picker.value;
      if (this.getAttribute('value') !== formatted) {
        this.setAttribute('value', formatted);
      }
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    if (name === 'value' && this.#picker && this.#input) {
      if (newValue) {
        this.#picker.setColor(newValue);
        // Don't overwrite input if user is typing
        if (document.activeElement !== this.#input) {
          this.#input.value = this.#picker.value;
        }
        this.#updateSwatch();
      }
    }

    if (name === 'disabled') {
      if (this.#input) {
        if (newValue !== null) {
          this.#input.setAttribute('disabled', '');
        } else {
          this.#input.removeAttribute('disabled');
        }
      }
    }
  }

  // Public API
  get value(): string {
    return this.#picker?.value ?? this.getAttribute('value') ?? '#000000';
  }

  set value(val: string) {
    if (this.#picker?.setColor(val)) {
      if (this.#input) {
        this.#input.value = this.#picker.value;
      }
      this.#updateSwatch();
      this.#syncValueAttribute();
    }
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  get rgb(): RGB {
    return this.#picker?.rgb ?? { r: 0, g: 0, b: 0 };
  }

  get hsl(): HSL {
    return this.#picker?.hsl ?? { h: 0, s: 0, l: 0 };
  }

  get hsv(): HSV {
    return this.#picker?.hsv ?? { h: 0, s: 0, v: 0 };
  }
}

customElements.define('ry-color-input', RyColorInput);
